import { toRaw } from "vue";
import dayjs from "dayjs";
import omit from "lodash/omit";
import {
  RESYNCED_TABLES,
  isPendingRecord,
  recordKey,
} from "@/db/pendingRecord";

const LOCAL_KEYS = [
  "isBuilt",
  "isStoredOffline",
  "isUpdatedOffline",
  "isCreatedOffline",
  "isDeletedOffline",
];

const toRelationValue = (value) => {
  if (Array.isArray(value)) return value.map(toRelationValue);
  return value instanceof BaseSchema ? value.uuid : value;
};

class BaseSchema {
  static primaryKey = "";
  static tableName = "";
  static fields = [];
  static compoundIndexes = [];
  static relatedFields = {};
  static dateFields = [];
  static dateTimeFields = [];
  isBuilt = false;

  static getFields() {
    return this.fields.concat([
      "isStoredOffline",
      "isUpdatedOffline",
      "isCreatedOffline",
      "isDeletedOffline",
    ]);
  }

  static formatFields() {
    let fields = this.getFields();
    const findDuplicates = (array) =>
      array.filter((item, index) => array.indexOf(item) !== index);
    const duplicates = findDuplicates(fields);
    if (duplicates) {
      duplicates.forEach((duplicate) =>
        console.warn(
          `Found duplicated field ${duplicate} in table ${this.tableName}`
        )
      );

      fields = fields.filter((element, index) => {
        return fields.indexOf(element) === index;
      });
    }
    return [...this.fields, ...this.compoundIndexes].join();
  }

  static toRecord(data) {
    const record = omit({ ...toRaw(data) }, "isBuilt");
    for (const key of Object.keys(this.relatedFields)) {
      if (!(key in record)) continue;
      if (this.fields.includes(key)) {
        record[key] = toRelationValue(record[key]);
      } else {
        delete record[key];
      }
    }
    return record;
  }

  static toPayload(data) {
    return omit(this.toRecord(data), LOCAL_KEYS);
  }

  static async createOrUpdate(data) {
    const obj = await this.formatData(toRaw(data));
    try {
      return await this._createOrUpdate(obj);
    } catch (e) {
      console.error(
        `Error while creating or updating in Dexie (table ${this.tableName})`,
        obj,
        e
      );
    }
  }

  static async formatData(data) {
    let obj = {};

    // INFO - B.L - 02/09/2022 - mapping fields
    Object.entries(data)
      .filter(([key]) => {
        return this.getFields().includes(key);
      })
      .forEach(([key, value]) => {
        if (value && this.dateFields.includes(key)) {
          if (dayjs(value).isValid()) {
            value = dayjs(value).toDate();
          } else {
            value = null;
          }
        }
        obj[key] = value;
      });

    Object.entries(this.relatedFields).forEach((mapper) => {
      if (mapper[1].many || mapper[1].m2m || !mapper[1].field) {
        return;
      }

      try {
        obj[mapper[1].field] = data[mapper[1].dataName].uuid;
      } catch {
        if (mapper[1].blank) return;
        if (!data[mapper[1].dataName]?.uuid) return;
        console.error(
          `Could not link uuid to ${mapper[1].field} value ${
            data[mapper[1].dataName]?.uuid
          } in table ${this.tableName}`
        );
      }
    });

    return obj;
  }

  static async _createOrUpdate(data) {
    const uuid = data[this.primaryKey];
    if (uuid == null) {
      console.warn(
        `Skipped a record without ${this.primaryKey} in ${this.tableName}`
      );
      return;
    }
    const table = this.prototype.db[this.tableName];

    // INFO - B.L - We are not using put as we want to avoid overwriting the data, put replace and delete all data
    const obj = await table.get({ [this.primaryKey]: uuid });
    if (!obj) {
      return await table.add(data).catch(async (error) => {
        if (error.name === "ConstraintError") {
          await table.update(uuid, data);
          return uuid;
        }
        console.error("An unexpected error occurred:", error);
      });
    }

    if (
      RESYNCED_TABLES.has(this.tableName) &&
      isPendingRecord(obj) &&
      !isPendingRecord(data) &&
      (await this.prototype.db.pendingSyncs.get(
        recordKey(this.tableName, uuid)
      ))
    )
      return uuid;
    if (await !obj.canBeUpdated()) return uuid;
    await table.update(uuid, data);
    return uuid;
  }

  async save() {
    return this.db[this.tableName].put(this.constructor.toRecord(this));
  }

  async canBeUpdated() {
    return true;
  }
  async canBeDeleted() {
    return true;
  }

  async getRelatedToDelete() {
    if (await !this.canBeDeleted()) return [];
    const toDelete = {};
    const cascadeRelated = Object.entries(this.relatedFields).filter(
      ([key, value]) => value.cascade === true && key
    );

    for (const [key, value] of cascadeRelated) {
      toDelete[value.tableName] = await this.getRelateds(key, value);
    }
    return toDelete;
  }

  async setisUpdatedOffline(hasChanged = true) {
    this.isUpdatedOffline = hasChanged;
    this.save();
  }

  async asForm(excluded = []) {
    excluded = excluded.concat(["createdAt", "updatedAt"]);

    // INFO - P.H - 20/07/2O23 - Date fields that have been modified offline must be formatted as strings in order to be synchronized with the back.
    const form = omit(this.constructor.toPayload(this), excluded);
    for (const dateField of this.dateFields) {
      if (!form[dateField]) continue;
      form[dateField] = this.dateTimeFields.includes(dateField)
        ? dayjs(form[dateField]).toISOString()
        : dayjs(form[dateField]).format("YYYY-MM-DD");
    }

    return form;
  }

  async setRelationships() {
    for (const [key, value] of Object.entries(this.relatedFields)) {
      this[key] = await this.getRelateds(key, value);
      if (
        !this[key] &&
        !value.blank &&
        this[value.field] !== null &&
        this[value.field] !== undefined &&
        this[value.field] !== ""
      ) {
        console.warn(
          `Could not find uuid (${value.field}) ${this[value.field]} in table ${value.tableName}`
        );
      }
    }
    this.isBuilt = true;
  }

  async setRelationshipsOf(keys) {
    for (const key of keys) this[key] = await this.getRelateds(key);
  }

  async getRelateds(key, value) {
    value = value || this.relatedFields[key];

    if (!value) {
      throw new Error(
        `No value found for key ${key} in table ${this.tableName}`
      );
    }
    if (value.many) {
      return await this.getManyToOne(value);
    } else if (value.m2m) {
      return await this.getManyToMany(value);
    } else {
      return await this.getOneToMany(value);
    }
  }

  async getManyToOne(value) {
    if (this.uuid == null) return [];
    return await this.db[value.tableName]
      .where({ [value.reversedKey]: this.uuid })
      .toArray();
  }

  async getOneToMany(value) {
    try {
      if (this[value.field])
        return await this.db[value.tableName].get({ uuid: this[value.field] });
      if (value.reversedKey && this.uuid != null)
        return await this.db[value.tableName].get({
          [value.reversedKey]: this.uuid,
        });
      return null;
    } catch {
      return null;
    }
  }

  async getManyToMany(value) {
    if (!value.owner) {
      if (this.uuid == null) return [];
      const uuid = this.uuid;
      return await this.db[value.tableName]
        .filter((item) => !!item[value.reversedKey]?.includes(uuid))
        .toArray();
    }

    const uuids = (this[value.relatedKey] || []).filter(Boolean);
    if (!uuids.length) {
      return [];
    }
    return await this.db[value.tableName].where("uuid").anyOf(uuids).toArray();
  }
}

export default BaseSchema;
